import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  money,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  AmountCell,
  FinancialStatementStyles,
  PresetChips,
  amount,
  periodPresets,
  printStatement,
  today,
} from "./FinancialStatementShared";

const TITLE = "Trial Balance";

const SECTIONS = [
  ["dr", "Trial Balance — Debit", "Debit balances"],
  ["cr", "Trial Balance — Credit", "Credit balances"],
];

// "" = Default: your own from / to dates. The others split a fiscal year
// (July to June) into columns, as CodeIgniter's trial_balance_faster2.
const PERIOD_TYPES = [
  ["", "Default"],
  ["monthly", "Monthly"],
  ["quarterly", "Quarterly"],
  ["half_yearly", "Half-Yearly"],
  ["yearly", "Yearly Periods"],
];

const FIELDS = ["opening", "debit", "credit", "closing"];

// The fiscal year running today starts on 1 July of this year.
const currentFiscalYear = () => {
  const now = new Date();
  return now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
};

const fiscalLabel = (year) =>
  `${year}-${String(year + 1).slice(-2)}`;

const FISCAL_YEARS = (() => {
  const years = [];
  for (let y = currentFiscalYear(); y >= 2000; y -= 1) years.push(y);
  return years;
})();

const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const nearZero = (value) => Math.abs(num(value)) < 0.005;

// Totals for a list of rows: named fields in default mode, per-column
// closings in period mode.
const totalsOf = (list, periodCount) =>
  periodCount
    ? {
        closings: Array.from({ length: periodCount }, (_, i) =>
          list.reduce((sum, row) => sum + num(row.closings[i]), 0),
        ),
      }
    : FIELDS.reduce(
        (acc, field) => ({
          ...acc,
          [field]: list.reduce((sum, row) => sum + num(row[field]), 0),
        }),
        {},
      );

const TbStyles = () => (
  <style>
    {`
      .tb-group-btn {
        padding: 0;
        border: 0;
        background: none;
        font: inherit;
        color: inherit;
        text-align: left;
        cursor: pointer;
      }
      .tb-group-btn i { width: 14px; color: #8b90a5; }
      .tb-count { margin-left: 8px; font-size: 0.7rem; font-weight: 600; color: #8b90a5; }
      .tb-badge {
        margin-left: 6px;
        padding: 1px 6px;
        border-radius: 8px;
        background: #fdf3e2;
        color: #c77700;
        font-size: 0.62rem;
        font-weight: 600;
        white-space: nowrap;
      }
      .tb-tools { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
      .tb-search { position: relative; min-width: 220px; }
      .tb-search i {
        position: absolute; top: 50%; left: 10px;
        transform: translateY(-50%); color: #b6bacb;
      }
      .tb-search .form-control { padding-left: 30px; height: 32px; font-size: 0.8rem; }
      .fs-table td.tb-dash { color: #d7dae8; }

      /* Period mode: scroll inside the panel, header and account columns stay put */
      .tb-scroll { max-height: 70vh; overflow: auto; }
      .tb-scroll .fs-table { min-width: 100%; }
      .tb-scroll thead th { position: sticky; top: 0; z-index: 3; }
      .tb-scroll .tb-sticky {
        position: sticky;
        left: 0;
        z-index: 2;
        background: #fff;
      }
      .tb-scroll .tb-sticky-2 { left: 90px; min-width: 240px; }
      .tb-scroll thead th.tb-sticky { z-index: 4; background: #2a2962; }
      .tb-scroll .fs-group .tb-sticky,
      .tb-scroll .fs-section .tb-sticky { background: #f7f8fc; }
      .tb-scroll .fs-total .tb-sticky { background: #2a2962; }
      .tb-scroll .fs-grand .tb-sticky { background: #eef0f8; }
      .tb-scroll td.tb-sticky:first-child,
      .tb-scroll th.tb-sticky:first-child { width: 90px; min-width: 90px; }
      .fs-table th.tb-future { color: #b6bacb; }
      .fs-table th .tb-period-sub {
        display: block;
        font-size: 0.6rem;
        font-weight: 500;
        letter-spacing: 0;
        text-transform: none;
        color: #b6bacb;
      }
      .fs-table td.tb-future-cell { color: #b6bacb; }
    `}
  </style>
);

// A zero movement shows as a faint dash; the ledger columns read better.
const MoveCell = ({ value }) =>
  nearZero(value) ? (
    <td className="coa-num tb-dash">–</td>
  ) : (
    <td className="coa-num">{money(value)}</td>
  );

const TrialBalance = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [, thisMonthFrom, thisMonthTo] = periodPresets()[0];
  const [inputs, setInputs] = useState({
    type: searchParams.get("type") || "",
    year: Number(searchParams.get("year")) || currentFiscalYear(),
    from: searchParams.get("from") || thisMonthFrom,
    to: searchParams.get("to") || thisMonthTo,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [search, setSearch] = useState("");
  const [hideZero, setHideZero] = useState(false);

  const fetchData = async (params = inputs) => {
    setLoading(true);
    setAlert(null);
    const query = params.type
      ? { type: params.type, year: params.year }
      : { from: params.from, to: params.to };
    setSearchParams(query, { replace: true });

    try {
      const res = await Http.get("/trial-balance", { params: query });
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the trial balance. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({
      ...values,
      [name]: name === "year" ? Number(value) : value,
    }));
  };

  const apply = (changes) => {
    const next = { ...inputs, ...changes };
    setInputs(next);
    fetchData(next);
  };

  const isPeriods = data?.mode === "periods";
  const periods = isPeriods ? data.periods : [];
  const periodCount = periods.length;

  // Latest column that is not in the future: the "as of now" figure.
  const currentIndex = useMemo(() => {
    if (!periodCount) return -1;
    const past = periods.map((p, i) => (p.future ? -1 : i)).filter((i) => i >= 0);
    return past.length ? past[past.length - 1] : periodCount - 1;
  }, [periods, periodCount]);

  const query = search.trim().toLowerCase();

  // Search and the zero filter narrow the accounts; group and section totals
  // follow what is shown.
  const sections = useMemo(() => {
    if (!data) return [];
    const isZeroRow = (account) =>
      isPeriods
        ? account.closings.every(nearZero)
        : nearZero(account.closing);

    return SECTIONS.map(([side, label, short]) => {
      const groups = data[side].groups
        .map((group) => {
          const groupMatches =
            query &&
            `${group.parent_code || ""} ${group.parent_name}`
              .toLowerCase()
              .includes(query);
          const accounts = group.accounts.filter(
            (account) =>
              (!hideZero || !isZeroRow(account)) &&
              (!query ||
                groupMatches ||
                `${account.code} ${account.name}`.toLowerCase().includes(query)),
          );
          return { ...group, accounts, ...totalsOf(accounts, periodCount) };
        })
        .filter((group) => group.accounts.length);
      return {
        side,
        label,
        short,
        groups,
        totals: totalsOf(groups, periodCount),
      };
    });
  }, [data, query, hideZero, isPeriods, periodCount]);

  const groupKey = (side, group) => `${side}-${group.parent_id || 0}`;

  const allGroupKeys = sections.flatMap((section) =>
    section.groups.map((group) => groupKey(section.side, group)),
  );
  const allCollapsed =
    allGroupKeys.length > 0 && allGroupKeys.every((key) => collapsed[key]);

  const toggleAll = () =>
    setCollapsed(
      allCollapsed
        ? {}
        : allGroupKeys.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    );

  const toggleGroup = (key) =>
    setCollapsed((values) => ({ ...values, [key]: !values[key] }));

  const filtered = !!query || hideZero;

  const differences = data
    ? isPeriods
      ? data.differences.map(num)
      : [num(data.difference)]
    : [];
  const unbalancedIndex = differences.findIndex((d) => !nearZero(d));
  const balanced = unbalancedIndex === -1;

  // Headline figures: the period itself (default) or the latest column.
  const headline = data
    ? isPeriods
      ? {
          dr: data.dr.totals.closings[currentIndex],
          cr: data.cr.totals.closings[currentIndex],
          at: periods[currentIndex]?.label,
        }
      : {
          dr: data.dr.totals.closing,
          cr: data.cr.totals.closing,
          at: formatDate(data.to),
        }
    : null;

  const typeLabel =
    PERIOD_TYPES.find(([value]) => value === (data?.type || ""))?.[1] || "Default";

  const periodLabel = data
    ? isPeriods
      ? `FY ${fiscalLabel(data.year)} (${formatDate(data.from)} – ${formatDate(data.to)}) · ${typeLabel}`
      : `${formatDate(data.from)} – ${formatDate(data.to)}`
    : "";

  // Amount columns for a row, in the current mode.
  const valuesOf = (row) =>
    isPeriods ? row.closings : FIELDS.map((field) => row[field]);

  const exportCsv = () => {
    const valueHeaders = isPeriods
      ? periods.map((p) => p.label)
      : ["Opening", "Debit", "Credit", "Closing"];
    const lines = [
      [TITLE],
      [isPeriods ? `FY ${fiscalLabel(data.year)} · ${typeLabel}` : `Period: ${data.from} to ${data.to}`],
      [],
      ["Section", "Group Code", "Group", "Account Code", "Head of Accounts", ...valueHeaders],
    ];
    sections.forEach((section) => {
      section.groups.forEach((group) => {
        group.accounts.forEach((account) =>
          lines.push([
            section.side.toUpperCase(),
            group.parent_code,
            group.parent_name,
            account.code,
            account.name,
            ...valuesOf(account).map((v) => num(v).toFixed(2)),
          ]),
        );
      });
      lines.push([
        section.side.toUpperCase(), "", "", "", `Total ${section.short}`,
        ...valuesOf(section.totals).map((v) => num(v).toFixed(2)),
      ]);
    });
    const csv = lines.map((line) => line.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = isPeriods
      ? `trial-balance_FY${fiscalLabel(data.year)}_${data.type}.csv`
      : `trial-balance_${data.from}_${data.to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const cells = (row) =>
      valuesOf(row)
        .map((value) => `<td class="num">${amount(value)}</td>`)
        .join("");
    const valueHeads = isPeriods
      ? periods.map((p) => `<th class="num">${p.label}</th>`).join("")
      : `<th class="num">Opening</th><th class="num">Debit</th>
         <th class="num">Credit</th><th class="num">Closing</th>`;
    const span = 2 + (isPeriods ? periodCount : 4);
    const headHtml = `<th>Code</th><th>Head of Accounts</th>${valueHeads}`;
    const bodyHtml = sections
      .map(
        (section) =>
          `<tr class="section"><td colspan="${span}">${section.label}</td></tr>` +
          section.groups
            .map(
              (group) =>
                `<tr class="group"><td class="code">${group.parent_code || ""}</td><td>${group.parent_name}</td>${cells(group)}</tr>` +
                group.accounts
                  .map(
                    (account) =>
                      `<tr class="account"><td class="code">${account.code}</td><td style="padding-left:24px">${account.name}</td>${cells(account)}</tr>`,
                  )
                  .join(""),
            )
            .join("") +
          `<tr class="total"><td></td><td>Total ${section.short}</td>${cells(section.totals)}</tr>`,
      )
      .join("");

    printStatement({
      title: TITLE,
      subtitle: `${isPeriods ? "" : "For the period "}${periodLabel}${filtered ? " &middot; Filtered" : ""} &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
      note: balanced
        ? isPeriods
          ? "Closing balances at the end of each period. Debit balances equal credit balances in every period."
          : "Debit balances equal credit balances."
        : `Debit and credit balances differ by ${amount(differences[unbalancedIndex])}${
            isPeriods ? ` in ${periods[unbalancedIndex].label}` : ""
          }.`,
    });
  };

  const stat = (label, icon, value, sub, className = "") => (
    <div className="col-md-3 mg-b-10">
      <div className={`coa-stat ${className}`}>
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{value}</span>
        <span className="coa-stat-sub">{sub}</span>
      </div>
    </div>
  );

  const periodDebit = data && !isPeriods
    ? num(data.dr.totals.debit) + num(data.cr.totals.debit)
    : 0;
  const periodCredit = data && !isPeriods
    ? num(data.dr.totals.credit) + num(data.cr.totals.credit)
    : 0;

  const columnCount = 2 + (isPeriods ? periodCount : 4);
  const sticky = (extra = "") => (isPeriods ? `tb-sticky ${extra}` : extra);

  // Amount cells of a group, account or total row in the current mode.
  const amountCells = (row, bold = false) =>
    isPeriods ? (
      row.closings.map((value, i) => (
        <td
          key={i}
          className={`coa-num ${num(value) < -0.005 ? "fs-neg" : ""} ${
            periods[i].future ? "tb-future-cell" : ""
          }`}
          style={bold ? { fontWeight: 700 } : undefined}
        >
          {amount(value)}
        </td>
      ))
    ) : (
      <>
        <AmountCell value={row.opening} />
        {bold ? (
          <>
            <td className="coa-num">{money(row.debit)}</td>
            <td className="coa-num">{money(row.credit)}</td>
          </>
        ) : (
          <>
            <MoveCell value={row.debit} />
            <MoveCell value={row.credit} />
          </>
        )}
        <AmountCell value={row.closing} />
      </>
    );

  // Built only once data has loaded: the check row reads data directly.
  const renderTable = () => (
    <table className="fs-table">
      <thead>
        <tr>
          <th className={sticky()}>Code</th>
          <th className={sticky("tb-sticky-2")}>Head of Accounts</th>
          {isPeriods ? (
            periods.map((period) => (
              <th
                key={period.start}
                className={`coa-num ${period.future ? "tb-future" : ""}`}
                title={`Closing balance at ${formatDate(period.end)}`}
              >
                {period.label}
                <span className="tb-period-sub">
                  {period.future ? "Upcoming" : `at ${formatDate(period.end)}`}
                </span>
              </th>
            ))
          ) : (
            <>
              <th className="coa-num">Opening</th>
              <th className="coa-num">Debit</th>
              <th className="coa-num">Credit</th>
              <th className="coa-num">Closing</th>
            </>
          )}
        </tr>
      </thead>
      <tbody style={{ opacity: loading ? 0.5 : 1 }}>
        {sections.map((section) => (
          <React.Fragment key={section.side}>
            <tr className="fs-section">
              <td className={sticky()} colSpan={isPeriods ? 2 : columnCount}>
                {section.label}
              </td>
              {isPeriods && <td colSpan={periodCount}></td>}
            </tr>
            {section.groups.map((group) => {
              const key = groupKey(section.side, group);
              const isCollapsed = !!collapsed[key];
              return (
                <React.Fragment key={key}>
                  <tr className="fs-group">
                    <td className={sticky("fs-code")}>{group.parent_code}</td>
                    <td className={sticky("tb-sticky-2")}>
                      <button
                        type="button"
                        className="tb-group-btn"
                        onClick={() => toggleGroup(key)}
                        aria-expanded={!isCollapsed}
                      >
                        <i
                          className={`fa fa-caret-${isCollapsed ? "right" : "down"}`}
                          aria-hidden="true"
                        ></i>
                        {group.parent_name}
                      </button>
                      <span className="tb-count">{group.accounts.length}</span>
                    </td>
                    {amountCells(group, isPeriods)}
                  </tr>
                  {!isCollapsed &&
                    group.accounts.map((account) => (
                      <tr key={account.id} className="fs-line">
                        <td className={sticky("fs-code")}>{account.code}</td>
                        <td className={sticky("tb-sticky-2")}>
                          <Link
                            to={`/chart-of-account-ledger/${account.id}`}
                            className="fs-ledger-link"
                            title="Open the account ledger"
                          >
                            {account.name}
                          </Link>
                          {!account.carries_opening && (
                            <span
                              className="tb-badge"
                              title="Opening balance is not carried forward for this account"
                            >
                              Movement only
                            </span>
                          )}
                          {account.status === "inactive" && (
                            <span className="fs-inactive">Inactive</span>
                          )}
                        </td>
                        {amountCells(account)}
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
            <tr className="fs-total">
              <td className={sticky()}></td>
              <td className={sticky("tb-sticky-2")}>Total {section.short}</td>
              {amountCells(section.totals, true)}
            </tr>
          </React.Fragment>
        ))}
        <tr className="fs-grand">
          <td className={sticky()}></td>
          <td className={sticky("tb-sticky-2")}>
            {balanced ? "Debit balances = Credit balances" : "Difference (Debit − Credit)"}
            {filtered && (
              <span className="fs-note" style={{ paddingLeft: 0 }}>
                Totals above follow the filters; the check uses every account
              </span>
            )}
          </td>
          {isPeriods ? (
            differences.map((value, i) => (
              <td key={i} className={`coa-num ${nearZero(value) ? "" : "fs-neg"}`}>
                {nearZero(value) ? (
                  <i className="fa fa-check" style={{ color: "#1f9d55" }} title="Balanced"></i>
                ) : (
                  amount(value)
                )}
              </td>
            ))
          ) : (
            <>
              <td colSpan={3}></td>
              <AmountCell value={balanced ? data.dr.totals.closing : differences[0]} />
            </>
          )}
        </tr>
      </tbody>
    </table>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <TbStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">{TITLE}</h5>
            <p className="text-muted mb-0 tx-12">
              {data
                ? isPeriods
                  ? periodLabel
                  : `For the period ${periodLabel}`
                : "Debit and credit balances of every account"}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={exportCsv}
              disabled={loading || !data}
            >
              <i className="fa fa-file-excel-o mg-r-6"></i>Export CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !data}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Statement Period
              </span>
              <span className="coa-head-note">
                {inputs.type
                  ? "Each column is the closing balance at the end of that period"
                  : "Opening = everything posted before the start date"}
              </span>
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-2">
                  <label>Period Type</label>
                  <select
                    className="form-control"
                    name="type"
                    value={inputs.type}
                    onChange={handleChange}
                  >
                    {PERIOD_TYPES.map(([value, label]) => (
                      <option key={value || "default"} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                {inputs.type ? (
                  <div className="col-lg-3 mg-t-10 mg-lg-t-0">
                    <label>Fiscal Year</label>
                    <select
                      className="form-control"
                      name="year"
                      value={inputs.year}
                      onChange={handleChange}
                    >
                      {FISCAL_YEARS.map((year) => (
                        <option key={year} value={year}>
                          {fiscalLabel(year)} (Jul {year} – Jun {year + 1})
                        </option>
                      ))}
                    </select>
                    <PresetChips
                      presets={[
                        ["This fiscal year", currentFiscalYear()],
                        ["Last fiscal year", currentFiscalYear() - 1],
                      ]}
                      isActive={([, year]) => inputs.year === year}
                      onPick={([, year]) => apply({ year })}
                    />
                  </div>
                ) : (
                  <div className="col-lg-6 mg-t-10 mg-lg-t-0">
                    <label>Period</label>
                    <div className="d-flex gap-2">
                      <input
                        type="date"
                        className="form-control"
                        name="from"
                        value={inputs.from}
                        onChange={handleChange}
                        max={inputs.to}
                        aria-label="From"
                      />
                      <input
                        type="date"
                        className="form-control"
                        name="to"
                        value={inputs.to}
                        onChange={handleChange}
                        min={inputs.from}
                        aria-label="To"
                      />
                    </div>
                    <PresetChips
                      presets={[["Today", today(), today()], ...periodPresets()]}
                      isActive={([, from, to]) =>
                        inputs.from === from && inputs.to === to
                      }
                      onPick={([, from, to]) => apply({ from, to })}
                    />
                  </div>
                )}
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchData()}
                    disabled={
                      loading || (!inputs.type && (!inputs.from || !inputs.to))
                    }
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {data && headline && (
            <div className="row mg-b-20">
              {stat(
                "Debit Balances",
                "fa-arrow-circle-o-down",
                amount(headline.dr),
                isPeriods
                  ? `Closing, ${headline.at}`
                  : `Closing, ${data.dr.groups.length} groups`,
              )}
              {stat(
                "Credit Balances",
                "fa-arrow-circle-o-up",
                amount(headline.cr),
                isPeriods
                  ? `Closing, ${headline.at}`
                  : `Closing, ${data.cr.groups.length} groups`,
              )}
              {isPeriods
                ? stat(
                    "Periods",
                    "fa-columns",
                    periodCount,
                    `${typeLabel} · FY ${fiscalLabel(data.year)}`,
                  )
                : stat(
                    "Period Movement",
                    "fa-exchange",
                    money(periodDebit),
                    `Debit · credit ${money(periodCredit)}`,
                  )}
              {stat(
                "Balance Check",
                "fa-balance-scale",
                balanced ? "Balanced" : "Out of balance",
                balanced
                  ? isPeriods
                    ? "Every period: debit = credit"
                    : "Debit balances equal credit balances"
                  : `Difference ${amount(differences[unbalancedIndex])}${
                      isPeriods ? ` in ${periods[unbalancedIndex].label}` : ""
                    }`,
                balanced ? "is-ok" : "is-alert",
              )}
            </div>
          )}

          {data && !balanced && (
            <div className="coa-notice is-alert">
              <i className="fa fa-exclamation-triangle"></i>
              <span>
                Debit and credit balances differ by{" "}
                <strong>{amount(differences[unbalancedIndex])}</strong>
                {isPeriods && ` in ${periods[unbalancedIndex].label}`}. This
                happens when opening amounts do not agree, or an account marked
                as not carrying its opening balance had postings before the
                start date.
              </span>
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-file-text-o me-2"></i>
                {TITLE}
                <span className="coa-head-note mg-l-10">Amounts in Taka</span>
              </span>
              {data && (
                <span className="tb-tools coa-no-print">
                  <label className="fs-switch">
                    <input
                      type="checkbox"
                      checked={hideZero}
                      onChange={(e) => setHideZero(e.target.checked)}
                    />
                    Hide zero closing
                  </label>
                  {allGroupKeys.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={toggleAll}
                    >
                      <i
                        className={`fa fa-${allCollapsed ? "expand" : "compress"} mg-r-6`}
                      ></i>
                      {allCollapsed ? "Show accounts" : "Groups only"}
                    </button>
                  )}
                  <div className="tb-search">
                    <i className="fa fa-search" aria-hidden="true"></i>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search code or account"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label="Search accounts"
                    />
                  </div>
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The trial balance could not be loaded.
              </div>
            ) : !sections.some((section) => section.groups.length) ? (
              <div className="coa-empty">
                <i className="fa fa-inbox"></i>
                {filtered
                  ? "No accounts match the filters."
                  : `No balances or postings up to ${formatDate(data.to)}.`}
              </div>
            ) : (
              <div className={isPeriods ? "tb-scroll" : "table-responsive"}>
                {renderTable()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialBalance;
